import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, Users, UserCheck, UserX, CheckCircle, XCircle, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployerDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/employer/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDashboard(response.data);
    } catch (error) {
      // console.error('Error fetching employer dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = dashboard?.employees?.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          emp.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
                          (statusFilter === 'active' && emp.membership_status === 'active') ||
                          (statusFilter === 'inactive' && emp.membership_status !== 'active');
    return matchesSearch && matchesStatus;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Building2 size={28} className="text-cyan-600" />
            <h1 className="text-3xl font-bold text-slate-900" data-testid="employer-company-name">
              {dashboard?.company_name || `${user?.first_name} ${user?.last_name}`}
            </h1>
          </div>
          <p className="text-slate-600">Employee Health Enrollment Dashboard</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border-2 border-cyan-200 p-6 shadow-sm" data-testid="stat-total-employees">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Users size={24} className="text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Enrolled</p>
                <p className="text-3xl font-bold text-slate-900">{dashboard?.total_employees || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border-2 border-green-200 p-6 shadow-sm" data-testid="stat-active-members">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Active Members</p>
                <p className="text-3xl font-bold text-slate-900">{dashboard?.active_members || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-6 shadow-sm" data-testid="stat-inactive-members">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <UserX size={24} className="text-slate-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Inactive</p>
                <p className="text-3xl font-bold text-slate-900">{dashboard?.inactive_members || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="pl-10"
                data-testid="employer-search-input"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'active', 'inactive'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setStatusFilter(filter)}
                  data-testid={`filter-${filter}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                    statusFilter === filter
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Employee List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Table Header - Desktop */}
          <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-4">Employee</div>
            <div className="col-span-2">Membership Status</div>
            <div className="col-span-3">Membership Period</div>
            <div className="col-span-3">Health Interview</div>
          </div>

          {filteredEmployees.length === 0 ? (
            <div className="text-center py-12" data-testid="no-employees-message">
              <Users size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'No employees match your search or filter.'
                  : 'No employees enrolled yet.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredEmployees.map((emp, index) => (
                <div
                  key={emp.user_id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`employee-row-${index}`}
                >
                  {/* Employee Name & Email */}
                  <div className="col-span-1 md:col-span-4">
                    <p className="font-medium text-slate-900" data-testid={`employee-name-${index}`}>{emp.name}</p>
                    <p className="text-sm text-slate-500">{emp.email}</p>
                  </div>

                  {/* Membership Status */}
                  <div className="col-span-1 md:col-span-2 flex items-center">
                    <div className="flex items-center gap-2 md:block">
                      <span className="text-xs text-slate-500 md:hidden">Status:</span>
                      {emp.membership_status === 'active' ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200" data-testid={`employee-status-${index}`}>Active</Badge>
                      ) : emp.membership_status === 'expiring_soon' ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`employee-status-${index}`}>Expiring Soon</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid={`employee-status-${index}`}>Inactive</Badge>
                      )}
                    </div>
                  </div>

                  {/* Membership Period */}
                  <div className="col-span-1 md:col-span-3 flex items-center">
                    <div>
                      <span className="text-xs text-slate-500 md:hidden">Period: </span>
                      <p className="text-sm text-slate-700">
                        {emp.membership_start_date || 'N/A'} — {emp.membership_expiry_date || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Health Interview */}
                  <div className="col-span-1 md:col-span-3 flex items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 md:hidden">Health Interview: </span>
                      {emp.health_interview_completed ? (
                        <span className="flex items-center gap-1.5 text-sm text-green-700" data-testid={`employee-interview-${index}`}>
                          <CheckCircle size={16} className="text-green-500" />
                          Completed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-sm text-slate-500" data-testid={`employee-interview-${index}`}>
                          <XCircle size={16} className="text-slate-400" />
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Footer */}
        <div className="mt-4 text-sm text-slate-500 text-right">
          Showing {filteredEmployees.length} of {dashboard?.total_employees || 0} employees
        </div>
      </div>
    </div>
  );
};

export default EmployerDashboard;
